/* eslint-disable @typescript-eslint/naming-convention */
import * as RadixDialog from '@radix-ui/react-dialog';
import { clsx } from 'clsx';
import { motion } from 'motion/react';
import type { HTMLAttributes, PropsWithChildren } from 'react';
import React, { forwardRef } from 'react';
import { spacing } from '../../styles/spacing.css';
import { Flex } from '../Flex/Flex';
import { IconButton } from '../IconButton/IconButton';
import { Layout } from '../Layout/Layout';
import * as styles from './Dialog.css';

export interface DialogContentProps extends RadixDialog.DialogContentProps {
  readonly size?: 'large' | 'small';
  readonly dynamicHeight?: boolean;
}

const Trigger: React.FC<RadixDialog.DialogTriggerProps> = ({ children, className, ...props }) => (
  <RadixDialog.Trigger asChild className={className} {...props}>
    {children}
  </RadixDialog.Trigger>
);

const Content: React.FC<DialogContentProps> = ({
  children,
  className,
  size = 'small',
  dynamicHeight = false,
  ...props
}) => (
  <RadixDialog.Content asChild {...props}>
    <Layout
      as={motion.div}
      className={clsx(styles.content({ size, dynamicHeight }), className)}
      layout
      left="50%"
      minWidth={{ md: '448px' }}
      p={spacing.s24}
      paddingBlockStart={spacing.s32}
      position="fixed"
      top="50%"
    >
      {children}
    </Layout>
  </RadixDialog.Content>
);

const Overlay = forwardRef<HTMLDivElement, RadixDialog.DialogOverlayProps>(({ children, className, ...props }, ref) => (
  <RadixDialog.Overlay className={clsx(styles.overlay, className)} ref={ref} {...props}>
    {children}
  </RadixDialog.Overlay>
));

export type DialogControlsProps = HTMLAttributes<HTMLDivElement>;

const Controls: React.FC<PropsWithChildren<DialogControlsProps>> = ({ className, children }) => (
  <Flex
    className={className}
    gap={spacing.s16}
    justifyContent="flex-end"
    padding={spacing.s12}
    position="absolute"
    right={spacing.s08}
    top={spacing.s08}
  >
    {children}
    <RadixDialog.Close aria-label="close" asChild>
      <IconButton background="transparent" className={styles.closeIcon} icon="close" size="lg" />
    </RadixDialog.Close>
  </Flex>
);

export const Portal: React.FC<RadixDialog.DialogPortalProps> = ({ children, ...props }) => (
  <RadixDialog.Portal {...props}>
    <RadixDialog.Overlay className={styles.mockOverlay}>{children}</RadixDialog.Overlay>
  </RadixDialog.Portal>
);

export const Dialog = {
  Root: RadixDialog.Root,
  Trigger,
  Content,
  Overlay,
  Title: RadixDialog.Title,
  Description: RadixDialog.Description,
  Portal,
  Close: RadixDialog.Close,
  Controls,
};
